/**
 * Routes. `/trade/:asset?anchor=` is the canonical deep link.
 *
 * The anchor is a query parameter and not a path segment because the two series
 * share one anchor grid: switching assets swaps the page to the sibling market
 * at the *same* anchor — `btc-updown-1m-1786637400` → `eth-updown-1m-1786637400`
 * — so the countdown does not jump (components.md §1). A URL that carries the
 * anchor alongside the asset makes that swap a parameter change rather than a
 * navigation, and it makes a support link reproducible: at 2,880 markets a day
 * "the BTC market at 21:46" has to be addressable.
 *
 * **`/trade` is the only route in the entry chunk — Phase 10.**
 *
 * Every other screen is behind `React.lazy`, and this is a performance budget
 * decision rather than a style one. Through Phase 9 the router imported all
 * eight statically, so the entry chunk carried Portfolio, History with its
 * ledger, the leaderboard, Rewards, Settings and the whole component gallery on
 * a cold load of the market page — screens the overwhelming majority of visitors
 * never open, on the one load whose byte count is measured
 * (`scripts/check-bundle-budget.mjs`).
 *
 * `<TradeRoute>` stays static on purpose: it is what `/` redirects to, so
 * splitting it would add a network round trip in front of first meaningful
 * paint, which is exactly the number Phase 4's exit criterion protects.
 *
 * **The fallback is deliberately not a spinner.** A chunk fetch on a warm
 * HTTP/2 connection is a few milliseconds; a spinner that flashes for one frame
 * on every navigation reads as slowness the product does not have. `<AppShell>`
 * with its header already painted holds the layout instead, so the page frame
 * never moves — the same rule §4.1 applies to a dropped socket.
 */

import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { TradeRoute } from '@/routes/trade/TradeRoute'
import { PlaceholderRoute } from '@/routes/PlaceholderRoute'
import { AppShell, Header } from '@/components/layout'

const GalleryRoute = lazy(() =>
  import('@/routes/gallery/GalleryRoute').then((m) => ({ default: m.GalleryRoute })),
)
const SettingsRoute = lazy(() =>
  import('@/routes/settings/SettingsRoute').then((m) => ({ default: m.SettingsRoute })),
)
const PortfolioRoute = lazy(() =>
  import('@/routes/portfolio/PortfolioRoute').then((m) => ({ default: m.PortfolioRoute })),
)
const HistoryRoute = lazy(() =>
  import('@/routes/history/HistoryRoute').then((m) => ({ default: m.HistoryRoute })),
)
const LeaderboardRoute = lazy(() =>
  import('@/routes/leaderboard/LeaderboardRoute').then((m) => ({ default: m.LeaderboardRoute })),
)
const StatsRoute = lazy(() =>
  import('@/routes/stats/StatsRoute').then((m) => ({ default: m.StatsRoute })),
)
const RewardsRoute = lazy(() =>
  import('@/routes/rewards/RewardsRoute').then((m) => ({ default: m.RewardsRoute })),
)
const MoneyRoute = lazy(() =>
  import('@/routes/money/MoneyRoute').then((m) => ({ default: m.MoneyRoute })),
)

/** The header and the page frame, with nothing in the content well yet. */
function RouteFrame() {
  return <AppShell header={<Header />}>{<div className="flex-1 bg-bg-surface" />}</AppShell>
}

export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFrame />}>
      <Routes>
        <Route path="/" element={<Navigate to="/trade/btc" replace />} />
        <Route path="/trade" element={<Navigate to="/trade/btc" replace />} />
        <Route path="/trade/:asset" element={<TradeRoute />} />
        <Route path="/gallery" element={<GalleryRoute />} />
        <Route path="/portfolio" element={<PortfolioRoute />} />
        <Route path="/leaderboard" element={<LeaderboardRoute />} />
        <Route path="/stats" element={<StatsRoute />} />
        {/*
          **`/money` is a real screen again — B8, and only because the collateral
          changed.**

          It redirected to `/history` from §F26l until now, with the right
          reasoning for the venue that existed then: funding was the user moving
          USDC in their own wallet, and there was nothing to withdraw from.

          The venue now settles in its own 1:1 wrapper over USDC
          (`contracts/src/` has it), so there is one step between holding USDC
          and being able to trade — Add funds — and one back, Withdraw. Since
          gasless funding each is one signature the venue sends and pays for;
          the money still moves only within the user's own wallet, so this is
          not the deposit screen that was struck out: there is still no vault,
          and `pending_withdrawal` and `withdrawable` are still gone.
        */}
        <Route path="/money" element={<MoneyRoute />} />
        <Route path="/history" element={<HistoryRoute />} />
        <Route path="/rewards" element={<RewardsRoute />} />
        <Route path="/settings" element={<SettingsRoute />} />
        {/*
          Rewards and profile are one screen. "What have I earned" and "what is my
          record" are the same question a user has after a session, and splitting
          them would be two half-pages.
        */}
        <Route path="/profile" element={<Navigate to="/rewards" replace />} />
        <Route path="*" element={<PlaceholderRoute title="Not found" phase="—" />} />
      </Routes>
    </Suspense>
  )
}
